import '../css/app.css';

import { createInertiaApp, router } from '@inertiajs/react';
import * as Sentry from '@sentry/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRoot } from 'react-dom/client';
import { Toaster } from './components/ui/sonner';
import { initializeTheme } from './hooks/use-appearance';
import './i18n'; // Initialize i18n
import { synchronizeLocale } from './i18n/synchronize';
import { LocaleData } from './types';

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV || 'production',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0'),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enabled: !!import.meta.env.VITE_SENTRY_DSN,
});

const appName = import.meta.env.VITE_APP_NAME || 'SmartKondo';

createInertiaApp({
    title: (title) => (title === 'SmartKondo' ? title : title ? `${title} - ${appName}` : appName),
    resolve: (name) => resolvePageComponent(`./pages/${name}.tsx`, import.meta.glob('./pages/**/*.tsx')),
    setup({ el, App, props }) {
        const root = createRoot(el, {
            onUncaughtError(error) {
                const diagnostic = document.createElement('pre');
                diagnostic.id = 'temporary-client-error';
                diagnostic.textContent = error instanceof Error ? `${error.message}\n${error.stack ?? ''}` : String(error);
                document.body.appendChild(diagnostic);
            },
        });
        synchronizeLocale(props.initialPage.props.locale as LocaleData | undefined);

        router.on('navigate', (event) => {
            synchronizeLocale(event.detail.page.props.locale as LocaleData | undefined);
        });

        root.render(
            <>
                <App {...props} />
                <Toaster position="top-right" richColors closeButton />
            </>,
        );
    },
    progress: {
        color: '#4B5563',
    },
});

// This will set light / dark mode on load...
initializeTheme();
